import React, { createContext, useContext } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAppSelector } from '@/store/store';
import { selectToken } from '@/pages/auth/authSlice';
import { Spinner } from '@packages/ui/primitives';
import { useOidcBootstrap } from '@apps/customer/hooks/useOidcBootstrap';
import { useAbpPermissions } from '@apps/customer/hooks/useAbpPermissions';
import { useCrossTabLogout } from '@apps/customer/hooks/useCrossTabLogout';

export interface AuthSession {
  token: string;
  isAuthenticated: true;
}

const AuthContext = createContext<AuthSession | null>(null);

/**
 * Reads the auth session from context.
 * Guaranteed non-null when called below `<AuthGate>` because
 * AuthGate only renders children once the session is ready.
 */
export function useAuthSession(): AuthSession {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthSession must be used within AuthGate');
  return ctx;
}

const PUBLIC_AUTH_ROUTES = ['/login', '/callback', '/redirect', '/check-user'];

interface AuthGateProps {
  children: React.ReactNode;
  publicFallback?: React.ReactNode;
}

/**
 * Render gate that blocks the authenticated subtree until the OIDC
 * session is loaded and ABP permissions are fetched.
 *
 * Lifecycle (state machine):
 *   idle -> oidc-loading -> abp-loading -> ready
 *                       \-> unauthenticated (no user)
 *                                        \-> error (permissions failed 3x)
 */
export function AuthGate({ children, publicFallback }: AuthGateProps) {
  const { pathname } = useLocation();
  const isPublicRoute = PUBLIC_AUTH_ROUTES.some((r) => pathname.startsWith(r));

  const { phase } = useOidcBootstrap({ skip: isPublicRoute });
  useAbpPermissions();
  useCrossTabLogout();

  const token = useAppSelector(selectToken);

  if (isPublicRoute) {
    return <>{publicFallback ?? children}</>;
  }

  if (phase === 'idle' || phase === 'oidc-loading' || phase === 'abp-loading') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (phase === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  if (phase === 'error') {
    const isSwitchedContext = window.location.hash.includes('cid=');
    if (isSwitchedContext) {
      return (
        <div className="flex items-center justify-center h-screen">
          <Spinner size="lg" />
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }

  const session: AuthSession = { token: token!, isAuthenticated: true };

  return (
    <AuthContext.Provider value={session}>
      {children}
    </AuthContext.Provider>
  );
}
